// This file contains material which is the pre-existing property of Psycle
// Interactive Limited.
// Copyright (c) 2018 Psycle Interactive. All rights reserved.

/* globals window */

// Import third party modules
import angular from 'angular';
import ngSanitize from 'angular-sanitize';
import uiRouter from '@uirouter/angularjs';
import smoothscroll from 'smoothscroll-polyfill';
import vcRecaptcha from 'angular-recaptcha';
import {enableBodyScroll, disableBodyScroll} from 'body-scroll-lock';

// Import first party modules
import prettyFocus from './pretty-focus/pretty-focus.service';
import webfont from './webfont/webfont.service';
import header from './header/header.component';
import home from './home/home.component';
import casestudy from './casestudy/casestudy.component';
import chatbots from './chatbots/chatbots.component';
import privacy from './privacy/privacy.component';
import cookiePolicy from './cookie-policy/cookie-policy.component';
import svg from './svg/svg.component';
import bgImage from './bg-image/bg-image.component';
import analytics from './analytics/analytics.component';
import video from './video/video.component';
import utilities from './utilities/utilities.directives';

// Import main application styles
import '../style/app.scss';

const MODULE_NAME = 'app';
angular

    .module(MODULE_NAME, [

      // Third party
      uiRouter,
      prettyFocus,
      webfont,
      vcRecaptcha,
      ngSanitize,

      // First party
      home,
      header,
      casestudy,
      chatbots,
      privacy,
      cookiePolicy,
      svg,
      bgImage,
      analytics,
      video,
      utilities,
    ])

    .config((
        $compileProvider,
        $stateProvider,
        $urlRouterProvider,
        $locationProvider
    ) => {

      $compileProvider.debugInfoEnabled(false);

      $stateProvider
          .state('home', {
            url: '/',
          })
          .state('chatbots', {
            url: '/chatbots',
          })
          .state('casestudy', {
            url: '/casestudy/:cid',
          })
          .state('privacy', {
            url: '/privacypolicy',
          })
          .state('cookiePolicy', {
            url: '/cookie-policy',
          });

      $urlRouterProvider.otherwise('/');
      $locationProvider.html5Mode({
        enabled: true,
        requireBase: false,
      });
    })

    .run(() => {
      smoothscroll.polyfill();
    })

    .controller('AppCtrl', function($state, $transitions, webfontService) {
      const ctrl = this;

      // ---------------------------------------------------------------------- //
      // Methods

      /**
     * onInit
     */
      function onInit() {
        webfontService.load();
        smoothscroll.polyfill();
        if (isTouchDevice()) {
          document.getElementsByTagName('html')[0].classList.add('is-touch');
        }

        isIOS();

        scrollEl = document.querySelector('.c-main');
      }

      /**
     * closeDrawers
     */
      function closeDrawers() {
        if (ctrl.drawers.full.active) {
          $state.go('chatbots');
        } else {
          $state.go('home');
        }
      }

      /**
     * lockBodyScroll
     */
      function lockBodyScroll() {
        if (scrollEl && !isIOS()) {
          disableBodyScroll(scrollEl);
        }
      }

      /**
     * unlockBodyScroll
     */
      function unlockBodyScroll() {
        if (scrollEl && !isIOS()) {
          enableBodyScroll(scrollEl);
        }
      }


      /**
     * Cookies notification
     */
      ctrl.cookiesNoticeDisplayed = false;
      ctrl.cookiesNoticeDismiss = () => {
        window.localStorage.setItem('cookies-notice', '1');
        ctrl.cookiesNoticeDisplayed = false;
      };
      if (window.localStorage.getItem('cookies-notice') === null) {
        ctrl.cookiesNoticeDisplayed = true;
      }

      /**
     * isTouchDevice
     * @return {boolean}
     */
      function isTouchDevice() {
        return (('ontouchstart' in window) ||
             (window.navigator.MaxTouchPoints > 0) ||
             (window.navigator.msMaxTouchPoints > 0));
      }

      /**
     * isIOS
     * @return {boolean}
     */
      function isIOS() {
        let version = false;
        if (/iP(hone|od|ad)/.test(window.navigator.platform)) {
          var v = (window.navigator.appVersion).match(/OS (\d+)_(\d+)_?(\d+)?/);
          version = parseFloat( parseInt(v[1], 10).toString() + '.' + parseInt(v[2], 10).toString() + '.' + parseInt(v[3] || 0, 10).toString() );
        }

        // Add HTML helper classes
        if ( version ) {
          document.getElementsByTagName('html')[0].classList.add('is-ios');
        }
        return version;
      }

      /**
     * navigate
     * @param {string} sectionId
     */
      function navigate(sectionId) {

        // Scroll to desired home section
        let targetEl = null;
        switch (sectionId) {
          case 'home':
            targetEl = document.querySelector('app-hero');
            break;
          case 'casestudies':
            targetEl = document.querySelector('app-casestudies');
            break;
          case 'about':
            targetEl = document.querySelector('app-about');
            break;
          case 'contact':
            targetEl = document.getElementById('contacts');
            break;
          case 'chatbots':
            $state.go('chatbots');
            break;
          case 'chatbotscontact':
            targetEl = document.querySelector('app-chatbots-contact');
            break;

        }
        if (targetEl) {
          targetEl.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          }, 10);
        }
      }


      // ---------------------------------------------------------------------- //
      // Private
      let scrollEl = null;

      // ---------------------------------------------------------------------- //
      // Public
      ctrl.drawers = {
        privacy: {
          active: false,
        },
        cookiePolicy: {
          active: false,
        },
        right: {
          active: false,
        },
        full: {
          active: false,
        },
      };
      ctrl.closeDrawers = closeDrawers;
      ctrl.navigate = navigate;

      // ---------------------------------------------------------------------- //
      // Watches/events
      $transitions.onSuccess({
        to: 'home',
      }, (trans) => {
        unlockBodyScroll();
        // Close drawers
        ctrl.drawers.privacy.active = false;
        ctrl.drawers.cookiePolicy.active = false;
        ctrl.drawers.right.active = false;
        ctrl.drawers.full.active = false;
      });

      $transitions.onSuccess({
        to: 'casestudy',
      }, (trans) => {
        lockBodyScroll();
        ctrl.drawers.privacy.active = false;
        ctrl.drawers.cookiePolicy.active = false;
        ctrl.drawers.right.active = true;
        ctrl.drawers.full.active = false;
      });

      $transitions.onSuccess({
        to: 'chatbots',
      }, (trans) => {
        lockBodyScroll();
        ctrl.drawers.privacy.active = false;
        ctrl.drawers.cookiePolicy.active = false;
        ctrl.drawers.right.active = false;
        ctrl.drawers.full.active = true;
      });

      $transitions.onSuccess({
        to: 'privacy',
      }, (trans) => {
        lockBodyScroll();
        ctrl.drawers.privacy.active = true;
        ctrl.drawers.cookiePolicy.active = false;
        ctrl.drawers.right.active = false;
        ctrl.drawers.full.active = false;
      });

      $transitions.onSuccess({
        to: 'cookiePolicy',
      }, (trans) => {
        lockBodyScroll();
        ctrl.drawers.privacy.active = false;
        ctrl.drawers.cookiePolicy.active = true;
        ctrl.drawers.right.active = false;
        ctrl.drawers.full.active = false;
      });

      // ---------------------------------------------------------------------- //
      onInit();
    });

export default MODULE_NAME;
